import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { legalPageRoutes, tabletRoutes } from '@utils/constants';
import Drawer from '@base/Drawer/Drawer';
import Login from '@cont/Login/Login';
import ClassVideoPlayer from '@cont/ClassVideoPlayer/ClassVideoPlayer';
import { breakpoints, Spacings } from '@styles/index';
import Dynamic from '@base/Dynamic/Dynamic';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useIsMobile } from '@stores/AppCommonData';
import Header from '../Header/Header';
import PageContent from '../Components/Content';
import ParentContainer from '../Components/AppContainer';
import BaseFooter from '../Footer/BaseFooter';
import LayoutContainer from '../Components/Container';

const Container = styled(LayoutContainer)`
  @media only screen and (max-width: ${breakpoints.tablet}) {
    min-width: 0px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100vw;
  }
`;

const NavbarWrapper = styled.div`
  position: relative;
  grid-column: 1/7;
  z-index: 2;
  height: 100%;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const HelpCenterNavBar = Dynamic(() => import('../NavBar/HelpCenterNavBar'));

const AppContainer = styled(ParentContainer)`
  margin-top: 0;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 100%;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: grid;
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-row-gap: 0;
    grid-template-columns: repeat(10, 1fr);
  }
`;

const Content = styled(PageContent)`
  margin-top: ${({ fullHeight }) => (fullHeight ? '0' : '40px')};
  grid-column: ${({ fullWidth }) => (fullWidth ? 1 : 7)} / 25;
  grid-template-columns: repeat(
    ${({ fullWidth }) => (fullWidth ? 24 : 18)},
    32px
  );
  ${({ position }) => (position ? `position: ${position};` : '')}
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin: auto;
    width: 100%;
    grid-template-columns: repeat(
      ${({ fullWidth }) => (fullWidth ? 24 : 18)},
      1fr
    );
  }
  @media only screen and (max-width: 728px) {
    grid-template-columns: none;
    width: 100%;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    grid-row-gap: 0;
    grid-column-gap: ${Spacings.SPACING_2B};
    display: block;
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const StyledDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    grid-template-rows: ${Spacings.SPACING_15B} 1fr;
  }
`;

const HelpCenterLayout = ({ children, layoutOptions = {} }) => {
  const router = useRouter();
  const { route, query } = router;
  const { isOpen: showLoginDrawer, closeElement: closeLoginDrawer } =
    useToggleable(LOGIN_DRAWER);
  const isMobile = useIsMobile();
  const [backButton, setBackButton] = useState({
    value: false,
    fn: () => {}
  });

  const handleBackPress = () => {
    backButton.fn();
  };

  /**
   * To check whether it is called from domain other than unacademy
   */
  const isExternal = query?.external === '1';

  const {
    isCheckoutUrl = false,
    hideFooter = false,
    hideHeader = false
  } = layoutOptions;
  const showHelpCenterNavbar =
    route.indexOf('/helpcenter/category/[categoryID]') > -1;

  const onClose = () => {
    closeLoginDrawer();
  };

  function renderLogin() {
    return (
      <StyledDrawer
        login
        drawerState={showLoginDrawer}
        onClose={onClose}
        showBackButton={backButton.value}
        hideCloseButton={backButton.value}
        onPressBack={handleBackPress}
      >
        <Login setBackButton={setBackButton} backButton={backButton} />
      </StyledDrawer>
    );
  }

  const insideApp = query.inside_app === 'true' || false;

  const backgroundColor = legalPageRoutes.includes(route)
    ? 'var(--color-base-0)'
    : null;
  const tabletWidth = tabletRoutes.includes(route) ? '728px' : '1136px';
  const showClassPlayer = isExternal ? true : !isMobile;

  if (isExternal) {
    return (
      <Container backgroundColor="var(--color-i-black-9)">
        {children}
        {showClassPlayer && <ClassVideoPlayer />}
      </Container>
    );
  }

  return (
    <Container backgroundColor={backgroundColor}>
      {!insideApp && !hideHeader && <Header isCheckoutUrl={isCheckoutUrl} />}
      <AppContainer
        backgroundColor={backgroundColor}
        $tabletWidth={tabletWidth}
        $isFooterHidden={hideFooter}
      >
        {showHelpCenterNavbar && (
          <NavbarWrapper>
            <HelpCenterNavBar />
          </NavbarWrapper>
        )}
        <Content fullWidth={!showHelpCenterNavbar}>{children}</Content>
        {showClassPlayer && <ClassVideoPlayer />}
      </AppContainer>
      {!insideApp && <BaseFooter display={!hideFooter} noFooter={hideFooter} />}
      {renderLogin()}
    </Container>
  );
};

export default HelpCenterLayout;
